import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react'
import { applyTooltips, Tooltip, DEFINITIONS } from '../utils/tooltips'
import { V, serif, mono, eyebrow, card, btnGold, btnGhost } from '../theme'
import { MANUAL_SECTIONS } from './RollCalculatorView'

function TradingRulesView({ state, onUpdate, onBack, tradingRulesData = [] }) {
  const [tab, setTab] = useState('all');
  const [search, setSearch] = useState('');
  const [collapsed, setCollapsed] = useState({});
  const [hoveredId, setHoveredId] = useState(null);
  const [showCustomForm, setShowCustomForm] = useState(false);
  const [customForm, setCustomForm] = useState({ text: '', source: '', explanation: '' });

  const favorites = state.favoriteRules || [];
  const customRules = state.customRules || [];

  const saveFavorites = (newFavs) => {
    onUpdate({ favoriteRules: newFavs });
    try { localStorage.setItem('tlaw_favoriteRules', JSON.stringify(newFavs)); } catch {}
  };
  const toggleFav = (id) => {
    const next = favorites.includes(id) ? favorites.filter(f => f !== id) : [...favorites, id];
    saveFavorites(next);
  };

  const saveCustomRule = () => {
    if (!customForm.text.trim()) return;
    const newRule = { id: `custom_${Date.now()}`, text: customForm.text.trim(), source: customForm.source.trim() || 'Personal Rule', explanation: customForm.explanation.trim() };
    const updated = [...customRules, newRule];
    onUpdate({ customRules: updated });
    try { localStorage.setItem('tlaw_customRules', JSON.stringify(updated)); } catch {}
    setCustomForm({ text: '', source: '', explanation: '' });
    setShowCustomForm(false);
  };
  const deleteCustomRule = (id) => {
    const updated = customRules.filter(r => r.id !== id);
    onUpdate({ customRules: updated });
    try { localStorage.setItem('tlaw_customRules', JSON.stringify(updated)); } catch {}
    saveFavorites(favorites.filter(f => f !== id));
  };

  const q = search.toLowerCase();
  const matchesSearch = (r) => !q || r.text.toLowerCase().includes(q) || r.source.toLowerCase().includes(q) || r.explanation.toLowerCase().includes(q);

  const toggleCollapse = (cat) => setCollapsed(c => ({ ...c, [cat]: !c[cat] }));

  const RuleCard = ({ rule, isCustom = false }) => {
    const isFav = favorites.includes(rule.id);
    const isHovered = hoveredId === rule.id;
    return (
      <div
        onMouseEnter={() => setHoveredId(rule.id)}
        onMouseLeave={() => setHoveredId(null)}
        style={{ background: V.bg2, border: `0.5px solid ${isFav ? V.gold : isHovered ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.12)'}`, borderLeft: isFav ? `3px solid ${V.gold}` : `0.5px solid ${isFav ? V.gold : isHovered ? 'rgba(201,168,76,0.35)' : 'rgba(201,168,76,0.12)'}`, borderRadius: '10px', padding: '20px', marginBottom: '12px', transition: 'border-color 0.15s', boxSizing: 'border-box', width: '100%' }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '12px', marginBottom: '6px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '8px', flex: 1, minWidth: 0 }}>
            {isCustom && <span style={{ fontSize: '10px', fontFamily: "'Jost',sans-serif", color: V.gold, background: 'rgba(201,168,76,0.1)', border: `0.5px solid ${V.border}`, borderRadius: '4px', padding: '2px 6px', flexShrink: 0, marginTop: '2px' }}>My Rule</span>}
            <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '15px', fontWeight: 500, color: V.gold, margin: 0, lineHeight: 1.4 }}>{rule.text}</p>
          </div>
          <button
            onClick={() => toggleFav(rule.id)}
            style={{ background: 'transparent', border: 'none', fontSize: '18px', cursor: 'pointer', padding: '0', lineHeight: 1, flexShrink: 0, opacity: isFav || isHovered ? 1 : 0.35, transition: 'opacity 0.15s' }}
            title={isFav ? 'Remove from My Rules' : 'Save to My Rules'}
          >
            {isFav ? '🔖' : '🔖'}
          </button>
        </div>
        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, fontStyle: 'italic', margin: '0 0 10px' }}>— {rule.source}</p>
        <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '13px', color: V.muted, lineHeight: 1.7, margin: 0 }}>{applyTooltips(rule.explanation)}</p>
        {isCustom && (
          <button onClick={() => deleteCustomRule(rule.id)} style={{ background: 'transparent', border: 'none', color: V.dim, fontFamily: "'Jost',sans-serif", fontSize: '11px', cursor: 'pointer', padding: '8px 0 0', display: 'block' }}>Delete</button>
        )}
      </div>
    );
  };

  const inp2 = { padding: '10px 12px', background: V.bg3, border: `0.5px solid ${V.border}`, borderRadius: '8px', color: V.text, fontSize: '13px', outline: 'none', width: '100%', boxSizing: 'border-box', fontFamily: "'Jost',sans-serif" };

  return (
    <div style={{ padding: '28px 24px', maxWidth: '100%', overflowX: 'hidden', boxSizing: 'border-box' }}>
      <button style={{ ...btnGhost, marginBottom: '20px', fontSize: '12px' }} onClick={onBack}>← Dashboard</button>
      <div style={{ ...eyebrow, marginBottom: '6px' }}>Reference</div>
      <h2 style={{ ...serif, fontSize: '32px', fontWeight: 500, color: V.gold, margin: '0 0 6px' }}>Trading Rules</h2>
      <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.muted, fontStyle: 'italic', marginBottom: '24px' }}>The timeless principles that protect your capital and sharpen your edge.</p>

      {/* Opening quote */}
      <div style={{ background: V.bg2, borderLeft: `3px solid ${V.gold}`, borderRadius: '10px', padding: '20px 24px', marginBottom: '28px' }}>
        <p style={{ ...serif, fontSize: '16px', fontStyle: 'italic', color: V.text, lineHeight: 1.8, margin: '0 0 8px' }}>"The game of speculation is the most uniformly fascinating game in the world. But it is not a game for the stupid, the mentally lazy, the person of inferior emotional balance, or the get-rich-quick adventurer. They will die poor."</p>
        <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '12px', color: V.muted }}>— Jesse Livermore</div>
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', borderBottom: `0.5px solid ${V.border}`, marginBottom: '20px' }}>
        {[{ id: 'all', label: 'All Rules' }, { id: 'mine', label: 'My Rules' }].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} style={{ padding: '10px 24px', background: 'transparent', border: 'none', borderBottom: tab === t.id ? `2px solid ${V.gold}` : '2px solid transparent', color: tab === t.id ? V.gold : V.muted, fontSize: '13px', cursor: 'pointer', fontFamily: "'Jost',sans-serif" }}>
            {t.label}{t.id === 'mine' && favorites.length > 0 ? ` (${favorites.length})` : ''}
          </button>
        ))}
      </div>

      {/* Search bar */}
      <input
        style={{ ...inp2, marginBottom: '24px' }}
        placeholder="Search rules..."
        value={search}
        onChange={e => setSearch(e.target.value)}
        onFocus={e => e.target.style.borderColor = V.gold}
        onBlur={e => e.target.style.borderColor = V.border.replace(')', ', 0.15)').replace('rgba', 'rgba')}
      />

      {/* ALL RULES TAB */}
      {tab === 'all' && tradingRulesData.map(cat => {
        const filtered = cat.rules.filter(matchesSearch);
        if (filtered.length === 0) return null;
        const isOpen = !collapsed[cat.category];
        return (
          <div key={cat.category} style={{ marginBottom: '8px' }}>
            <button
              onClick={() => toggleCollapse(cat.category)}
              style={{ display: 'flex', alignItems: 'center', gap: '10px', width: '100%', background: 'transparent', border: 'none', borderLeft: isOpen ? `3px solid ${V.gold}` : `3px solid transparent`, borderRadius: '4px', padding: '12px 16px', cursor: 'pointer', textAlign: 'left', marginBottom: isOpen ? '4px' : '0' }}
            >
              <span style={{ ...serif, fontSize: '20px', color: V.gold, fontWeight: 500, flex: 1 }}>{cat.category}</span>
              <span style={{ color: V.muted, fontSize: '14px', transition: 'transform 0.2s', display: 'inline-block', transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)' }}>▸</span>
            </button>
            {isOpen && (
              <div style={{ paddingTop: '4px' }}>
                {filtered.map(rule => <RuleCard key={rule.id} rule={rule} />)}
              </div>
            )}
          </div>
        );
      })}

      {/* MY RULES TAB */}
      {tab === 'mine' && (() => {
        const favBuiltIn = tradingRulesData.flatMap(c => c.rules).filter(r => favorites.includes(r.id) && matchesSearch(r));
        const favCustom = customRules.filter(r => favorites.includes(r.id) && matchesSearch(r));
        const allCustom = customRules.filter(matchesSearch);
        const hasSaved = favBuiltIn.length > 0 || favCustom.length > 0;
        return (
          <div>
            {/* Custom rules always shown at top */}
            {allCustom.length > 0 && (
              <div style={{ marginBottom: '20px' }}>
                <div style={{ ...eyebrow, marginBottom: '12px' }}>My Personal Rules</div>
                {allCustom.map(rule => <RuleCard key={rule.id} rule={rule} isCustom />)}
              </div>
            )}
            {/* Bookmarked built-in rules */}
            {hasSaved || favCustom.length > 0 ? (
              <div>
                {favBuiltIn.length > 0 && <div style={{ ...eyebrow, marginBottom: '12px' }}>Saved Rules</div>}
                {favBuiltIn.map(rule => <RuleCard key={rule.id} rule={rule} />)}
                {!hasSaved && allCustom.length === 0 && (
                  <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                    <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.muted, lineHeight: 1.7 }}>Tap the 🔖 on any rule to save it here.<br />Build your personal rulebook over time.</p>
                  </div>
                )}
              </div>
            ) : allCustom.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px 20px' }}>
                <p style={{ fontFamily: "'Jost',sans-serif", fontSize: '14px', color: V.muted, lineHeight: 1.7 }}>Tap the 🔖 on any rule to save it here.<br />Build your personal rulebook over time.</p>
              </div>
            )}
            {/* Add Your Own Rule */}
            {!showCustomForm ? (
              <button style={{ ...btnGold, marginTop: '20px', fontSize: '13px', padding: '10px 20px' }} onClick={() => setShowCustomForm(true)}>+ Add Your Own Rule</button>
            ) : (
              <div style={{ background: V.bg2, border: `0.5px solid ${V.border}`, borderRadius: '10px', padding: '20px', marginTop: '20px', boxSizing: 'border-box', width: '100%' }}>
                <div style={{ ...eyebrow, marginBottom: '14px' }}>Add Your Own Rule</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <div>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Rule</div>
                    <input style={inp2} placeholder="Your trading rule..." value={customForm.text} onChange={e => setCustomForm(f => ({ ...f, text: e.target.value }))} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Source <span style={{ textTransform: 'none', opacity: 0.6 }}>(optional)</span></div>
                    <input style={inp2} placeholder="Personal Rule" value={customForm.source} onChange={e => setCustomForm(f => ({ ...f, source: e.target.value }))} />
                  </div>
                  <div>
                    <div style={{ fontFamily: "'Jost',sans-serif", fontSize: '11px', color: V.muted, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '4px' }}>Explanation</div>
                    <textarea style={{ ...inp2, minHeight: '80px', resize: 'vertical' }} placeholder="Why this rule matters to you..." value={customForm.explanation} onChange={e => setCustomForm(f => ({ ...f, explanation: e.target.value }))} />
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    <button style={{ ...btnGold, padding: '10px 20px', fontSize: '13px' }} onClick={saveCustomRule}>Save My Rule →</button>
                    <button style={{ ...btnGhost, padding: '10px 18px', fontSize: '13px' }} onClick={() => { setShowCustomForm(false); setCustomForm({ text: '', source: '', explanation: '' }); }}>Cancel</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

function ManualView({ onBack }) {
  const [section, setSection] = useState('rules');
  const sec = MANUAL_SECTIONS.find(s => s.id === section);
  return (
    <div style={{ padding: '28px 24px', maxWidth: '780px', margin: '0 auto' }}>
      <button style={btnGhost} onClick={onBack}>← Dashboard</button>
      <div style={{ marginTop: '20px', ...eyebrow, marginBottom: '6px' }}>Reference</div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, color: V.text, margin: '0 0 20px' }}>Trading Manual</h2>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '24px' }}>
        {MANUAL_SECTIONS.map(s => (
          <button key={s.id} onClick={() => setSection(s.id)}
            style={{ padding: '7px 14px', borderRadius: '7px', border: `0.5px solid ${section === s.id ? V.gold : V.border}`, background: section === s.id ? 'rgba(201,168,76,0.1)' : 'transparent', color: section === s.id ? V.goldL : V.muted, fontSize: '12px', cursor: 'pointer' }}>
            {s.icon} {s.title}
          </button>
        ))}
      </div>
      {sec.content.map((item, i) => (
        <div key={i} style={card}>
          <div style={{ ...serif, fontSize: '18px', color: V.goldL, marginBottom: '10px', fontWeight: 500 }}>{item.h}</div>
          <p style={{ fontSize: '13px', lineHeight: 1.85, color: 'rgba(240,237,230,0.82)' }}>{item.body}</p>
        </div>
      ))}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// TRADE SETUP CHECKLIST
// ═══════════════════════════════════════════════════════════════
const SETUP_ITEMS = {
  csp: [
    { id: 1, text: 'Stock is above the 200-day moving average' },
    { id: 2, text: 'IV Rank is between 30–70 (good premium environment)' },
    { id: 3, text: 'No earnings announcement within the next 30 days' },
    { id: 4, text: 'Open interest on target strike is above 500 contracts' },
    { id: 5, text: 'Strike is below a clear support level on the chart' },
    { id: 6, text: 'I would be happy owning 100 shares at this strike price' },
    { id: 7, text: 'Position size is 10–15% of my account or less' },
    { id: 8, text: 'DTE is 30–45 days (not under 21 on new entries)' },
    { id: 9, text: 'I have defined my exit: close at 50% profit or 2× loss' },
    { id: 10, text: 'Premium collected covers at least $50 after commissions' },
  ],
  cc: [
    { id: 1, text: 'I own 100 shares of the underlying stock' },
    { id: 2, text: 'My strike is at or above my cost basis in the shares' },
    { id: 3, text: 'I am comfortable having my shares called away at this strike' },
    { id: 4, text: 'No earnings announcement within the next 30 days' },
    { id: 5, text: 'Target delta is ~0.15 for beginners — approximately 85% probability call expires worthless' },
    { id: 6, text: 'DTE is 30–45 days for optimal theta decay' },
    { id: 7, text: 'Premium collected is meaningful relative to share price' },
    { id: 8, text: 'I have a plan if shares get called away (restart the Wheel?)' },
  ],
  spread: [
    { id: 1, text: 'I am trading SPX (not a single stock — too much gap risk)' },
    { id: 2, text: 'VIX is below 25 (if above 25, skip or trade smaller)' },
    { id: 3, text: 'No major economic events today (Fed, CPI, FOMC)' },
    { id: 4, text: 'Time is between 9:30–11:00 AM or after 2:00 PM ET' },
    { id: 5, text: 'Strike is at least 30–50 points OTM from current SPX' },
    { id: 6, text: 'Credit received is at least $0.25 per spread' },
    { id: 7, text: 'Max risk per trade is 1-2% of account' },
    { id: 8, text: 'I have set a 2× loss rule and will not override it' },
    { id: 9, text: 'I will close at 50% profit and not chase maximum gain' },
  ],
};

function TradeSetupView({ onBack }) {
  const [strategy, setStrategy] = useState('csp');
  const [checked, setChecked]   = useState({});
  const items  = SETUP_ITEMS[strategy];
  const count  = items.filter(item => checked[`${strategy}_${item.id}`]).length;
  const pct    = Math.round((count / items.length) * 100);
  const ready  = count === items.length;

  const toggle = (id) => setChecked(c => ({ ...c, [`${strategy}_${id}`]: !c[`${strategy}_${id}`] }));
  const reset  = () => setChecked(c => { const n = { ...c }; items.forEach(i => delete n[`${strategy}_${i.id}`]); return n; });

  const labels = { csp: 'Cash-Secured Put', cc: 'Covered Call', spread: '0DTE Credit Spread' };

  return (
    <div style={{ padding: '28px 24px', maxWidth: '740px', margin: '0 auto' }}>
      <button style={btnGhost} onClick={onBack}>← Dashboard</button>
      <div style={{ marginTop: '20px', ...eyebrow, marginBottom: '6px' }}>Pre-Trade</div>
      <h2 style={{ ...serif, fontSize: '28px', fontWeight: 500, color: V.text, margin: '0 0 6px' }}>Trade Setup Checklist</h2>
      <p style={{ fontSize: '13px', color: V.muted, marginBottom: '20px' }}>Run through this before every trade. All boxes checked = ready to enter.</p>

      <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', flexWrap: 'wrap' }}>
        {Object.entries(labels).map(([key, label]) => (
          <button key={key} onClick={() => setStrategy(key)}
            style={{ padding: '8px 18px', borderRadius: '6px', border: `0.5px solid ${strategy === key ? V.gold : V.border}`, background: strategy === key ? 'rgba(201,168,76,0.1)' : 'transparent', color: strategy === key ? V.goldL : V.muted, fontSize: '13px', cursor: 'pointer' }}>
            {label}
          </button>
        ))}
      </div>

      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
        <span style={{ fontSize: '12px', color: ready ? V.sage : V.muted }}>{count}/{items.length} complete</span>
        <div style={{ flex: 1, height: '3px', background: V.bg3, borderRadius: '2px', margin: '0 12px' }}>
          <div style={{ height: '100%', width: `${pct}%`, background: ready ? `linear-gradient(90deg,${V.sage},#a8d8b8)` : `linear-gradient(90deg,${V.gold},${V.goldL})`, borderRadius: '2px', transition: 'width .3s ease' }}/>
        </div>
        <button onClick={reset} style={{ fontSize: '11px', color: V.dim, background: 'transparent', border: 'none', cursor: 'pointer', padding: '2px 6px' }}>Reset</button>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '20px' }}>
        {items.map(item => {
          const isChecked = !!checked[`${strategy}_${item.id}`];
          return (
            <div key={item.id} onClick={() => toggle(item.id)}
              style={{ display: 'flex', alignItems: 'flex-start', gap: '12px', padding: '13px 16px', borderRadius: '8px', border: `0.5px solid ${isChecked ? 'rgba(122,171,138,0.4)' : V.border}`, background: isChecked ? 'rgba(122,171,138,0.07)' : V.bg2, cursor: 'pointer', transition: 'all .15s' }}>
              <div style={{ width: '18px', height: '18px', borderRadius: '4px', border: `1.5px solid ${isChecked ? V.sage : V.border}`, background: isChecked ? V.sage : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: '1px', transition: 'all .15s' }}>
                {isChecked && <span style={{ fontSize: '11px', color: '#0a0a0c', fontWeight: 700 }}>✓</span>}
              </div>
              <span style={{ fontSize: '13px', color: isChecked ? 'rgba(240,237,230,0.6)' : V.text, lineHeight: 1.55, textDecoration: isChecked ? 'line-through' : 'none' }}>{item.text}</span>
            </div>
          );
        })}
      </div>

      {ready && (
        <div style={{ textAlign: 'center', padding: '20px', background: 'rgba(122,171,138,0.08)', borderRadius: '10px', border: `0.5px solid rgba(122,171,138,0.3)` }}>
          <div style={{ ...serif, fontSize: '22px', color: V.sage, marginBottom: '6px' }}>✓ All Clear — Ready to Trade</div>
          <p style={{ fontSize: '13px', color: V.muted, lineHeight: 1.7 }}>You've verified every condition. Enter the trade with confidence and follow your exit rules.</p>
        </div>
      )}
    </div>
  );
}

export { TradingRulesView, ManualView, TradeSetupView }
